import { useState, useRef } from "react";
import { Controller } from "react-hook-form";
import { Button } from "primereact/button";
import { api, BASE_URL } from "../../env";
import { Toast } from "primereact/toast";
import { useTranslation } from "react-i18next";

export const ControlledFileUploadWithView = ({
  name,
  control,
  rules,
  label,
  required = false,
  derogationId,
  fieldName,
}) => {
  const {  t } = useTranslation();
  const toast = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef();

  const isImage = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    return ["jpg", "jpeg", "png"].includes(ext);
  };

  const handleDownload = async () => {
    if (!derogationId || !fieldName) return;
    const fileFieldMap = {
      carteNational: "carte_national_path",
      extraitNaissance: "extrait_naissance_path",
      extraitNaissanceEpouse: "extrait_naissance_epouse_path",
      contratLoyer: "copie_contrat_loyer_path",
      quittance: "copie_derniere_quittance_path",
    };

    const camelCaseKey = "contratLoyer";
    fieldName = fileFieldMap[camelCaseKey];

    const downloadUrl = `${BASE_URL}/api/derogations/${derogationId}/download/${fieldName}`;

    try {
      const response = await api.get(downloadUrl, { responseType: "blob" });
      if (response.status !== 200)
        throw new Error("Network response was not ok");

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      window.open(url);
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (error) {
      toast.current?.show({
        severity: "warn",
        summary: t('attention'),
        detail: t('file_size_limit'),
        life: 5000,
      });
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <div className="mb-4">
          <label className="block mb-2 font-medium text-gray-800">
            {label} {required && <span className="text-red-500">*</span>}
          </label>

          {/* Custom Upload Circle */}
          <div className="flex justify-center my-4">
            <label
              htmlFor={`${name}-input`}
              className={`cursor-pointer flex flex-col items-center justify-center rounded-full border-2 ${
                fieldState.invalid ? "border-red-400" : "border-green-400"
              } hover:shadow-lg transition`}
            >
              <i className="pi pi-cloud-upload text-green-500 text-3xl mb-1" />
              <span className="text-sm text-green-600 font-semibold mt-2">
                {t('charger')}
              </span>
              <input
                id={`${name}-input`}
                type="file"
                accept=".pdf"
                hidden
                ref={fileInputRef}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file && file.size > 512 * 1024) {
                    field.onChange(null);
                    toast.current?.show({
                      severity: "warn",
                      summary: t('attention'),
                      detail: t('file_size_limit'),
                      life: 5000,
                    });
                  } else {
                    field.onChange(file);
                  }
                }}
              />
            </label>
          </div>

          {/* Show existing file link (if no new file is selected) */}
          {!field.value && derogationId && fieldName && (
            <p>
              <div className="flex items-center gap-2 mb-2">
                <strong className="text-gray-700">{label}:</strong>
                <Button
                  label={t('afficher_fichier')}
                  icon="pi pi-eye"
                  className="p-button-sm p-button-text p-button-info"
                  onClick={handleDownload}
                />
              </div>
            </p>
          )}

          {/* File name and preview (for newly uploaded file) */}
          {field.value && (
            <div className="mt-2 flex flex-col gap-2 items-start">
              <span className="text-sm text-gray-700 flex items-center gap-2">
                📄 {field.value.name}
                <Button
                  type="button"
                  icon="pi pi-eye"
                  className="p-button-text p-button-sm"
                  label={t('file_preview')}
                  onClick={() => setShowPreview(true)}
                />
              </span>

              {/* Image inline preview */}
              {isImage(field.value.name) && (
                <img
                  src={URL.createObjectURL(field.value)}
                  alt="preview"
                  className="rounded border"
                  style={{ maxWidth: "100%", maxHeight: "150px" }}
                />
              )}
            </div>
          )}

          {/* Validation message */}
          {fieldState.error && (
            <small className="p-error block mt-1">
              {fieldState.error.message ||
                t('this_field_required')}
            </small>
          )}

          {/* Modal-style large preview */}
          {showPreview && field.value && (
            <div className="mt-3 border p-2 rounded bg-gray-100">
              {isImage(field.value.name) ? (
                <img
                  src={URL.createObjectURL(field.value)}
                  alt="preview"
                  className="w-full h-auto"
                  style={{ maxHeight: "400px" }}
                />
              ) : (
                <iframe
                  src={URL.createObjectURL(field.value)}
                  title="File Preview"
                  className="w-full"
                  style={{ height: "400px", border: "none" }}
                />
              )}
              <Button
                onClick={() => setShowPreview(false)}
                label={t('close')}
                className="mt-2 p-button-sm p-button-secondary"
              />
            </div>
          )}
          <Toast ref={toast} />
        </div>
      )}
    />
  );
};
